/**
 * Single source of truth for internal, locale-aware paths.
 *
 * Pure module: safe to import from server and client components. It only depends on
 * `lib/data` and `lib/i18n`, both of which have no imports of their own.
 *
 * Every function returns a relative path that already carries the locale prefix, so the link
 * never depends on the proxy redirect. No domain is ever included.
 */
import { getToolUrlPath } from '@/lib/data';
import { toFrenchProfessionSlug, type Locale } from '@/lib/i18n';

/** Minimal tool shape needed to build a tool URL. */
export interface ToolLinkSource {
  slug: string;
  profession_slug: string;
  categories: string[] | null;
}

/** Prefix an in-site path (starting with `/`) with the locale: `/{locale}{path}`. */
export function localePath(locale: Locale, path: string): string {
  return `/${locale}${path}`;
}

/** `/{locale}/ai-for-{profession}` */
export function professionHref(locale: Locale, professionSlug: string): string {
  const slug = locale === 'fr' ? toFrenchProfessionSlug(professionSlug) : professionSlug;
  return localePath(locale, `/ai-for-${slug}`);
}

/** `/{locale}/ai-for-{profession}/{category-slug}` */
export function categoryHref(locale: Locale, professionSlug: string, categorySlug: string): string {
  return `${professionHref(locale, professionSlug)}/${categorySlug}`;
}

/**
 * `/{locale}/ai-for-{profession}/{category-slug}[/{sub-slug}]/{tool-slug}`
 * Category segments come from `getToolUrlPath` (primary category = categories[0]).
 */
export function toolHref(locale: Locale, tool: ToolLinkSource): string {
  return `${professionHref(locale, tool.profession_slug)}/${getToolUrlPath(
    tool.profession_slug,
    tool.categories ?? [],
    tool.slug
  )}`;
}

/** `/{locale}/tools` */
export function toolsHref(locale: Locale): string {
  return localePath(locale, '/tools');
}

/**
 * `/{locale}/tools?q={query}`
 * The query is URL-encoded; the caller is responsible for trimming/validating it.
 */
export function searchHref(locale: Locale, query: string): string {
  return `${toolsHref(locale)}?q=${encodeURIComponent(query)}`;
}

/** `/{locale}/insights` */
export function insightsHref(locale: Locale): string {
  return localePath(locale, '/insights');
}

/** `/{locale}/insights/{slug}` */
export function insightHref(locale: Locale, slug: string): string {
  return localePath(locale, `/insights/${slug}`);
}

/** `/{locale}/submit-tool` */
export function submitToolHref(locale: Locale): string {
  return localePath(locale, '/submit-tool');
}

/** `/{locale}/about` */
export function aboutHref(locale: Locale): string {
  return localePath(locale, '/about');
}

/**
 * `/{locale}/compare/{left}-vs-{right}`
 * Pure builder: it does no special handling of the `select` placeholder. Callers decide when to navigate.
 */
export function comparePairHref(locale: Locale, left: string, right: string): string {
  return localePath(locale, `/compare/${encodeURIComponent(left)}-vs-${encodeURIComponent(right)}`);
}

/**
 * `/{locale}/compare?tool={slug}`
 * The compare page reads `searchParams.tool` and preselects it as the left tool.
 */
export function compareHref(locale: Locale, toolSlug: string): string {
  return `${localePath(locale, '/compare')}?tool=${encodeURIComponent(toolSlug)}`;
}
